import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from 'react-oauth2-code-pkce';
import GlobalContext from '../context/GlobalContext';
import UsersService from '../services/users-service';
import CompaniesService from '../services/companies-service';
import AdminShell from './AdminShell';
import DeniedCard from './DeniedCard';
import { IconEdit } from './Icons';

export default function UserDetailPage() {
  const { userId } = useParams();
  const { currentUser, userLoading } = useContext(GlobalContext);
  const { token } = useContext(AuthContext);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ firstName: '', lastName: '', isAdmin: false, isReadonly: false, selectedCompanies: [] });
  const [successMessage, setSuccessMessage] = useState('');
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creatingCompany, setCreatingCompany] = useState(false);
  
  // Password reset state
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Fetch user details on component mount
  useEffect(() => {
    if (token && currentUser?.isAdmin && userId) {
      fetchUserDetails();
      fetchCompanies();
    }
  }, [token, currentUser, userId]);

  // Update edit data when user changes
  useEffect(() => {
    if (user) {
      setEditData({
        firstName: user.keycloakUser?.firstName || '',
        lastName: user.keycloakUser?.lastName || '',
        isAdmin: user.internalUser?.isAdmin || false,
        isReadonly: user.internalUser?.isReadonly || false,
        selectedCompanies: user.internalUser?.companies?.map(c => c.id) || []
      });
    }
  }, [user]);

  // Handle keyboard shortcuts for editing modal
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !saving && !creatingCompany && !activating && !resetting) {
        if (isCreatingCompany) {
          handleCancelCreateCompany();
        } else {
          handleCancel();
        }
      } else if (e.key === 's' && (e.ctrlKey || e.metaKey) && !saving && !creatingCompany && !activating && !resetting) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'n' && (e.ctrlKey || e.metaKey) && !saving && !creatingCompany && !activating && !resetting) {
        e.preventDefault();
        setIsCreatingCompany(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, saving, creatingCompany, isCreatingCompany, activating, resetting]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Since there's no individual user endpoint, we'll search for the user
      // by fetching all users and finding the one with matching ID
      const usersPage = await UsersService.getAllUsers(token, '', 0, 1000); // Get a large page to find our user
      const foundUser = usersPage.content.find(user => user.keycloakUser?.id === userId);
      
      if (foundUser) {
        setUser(foundUser);
      } else {
        setError('Gebruiker niet gevonden');
      }
    } catch (err) {
      setError('Fout bij laden van gebruikersgegevens: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const companiesData = await CompaniesService.getAllCompanies(token);
      setCompanies(companiesData);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const handleSave = async () => {
    if (!user || !user.keycloakUser?.id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // Create the updated user data according to the API schema
      const updatedUser = {
        keycloakUser: {
          ...user.keycloakUser,
          firstName: editData.firstName || null,
          lastName: editData.lastName || null
        },
        internalUser: {
          ...user.internalUser,
          isAdmin: editData.isAdmin,
          isReadonly: editData.isReadonly,
          companies: companies.filter(company => 
            editData.selectedCompanies.includes(company.id)
          )
        }
      };
      
      // If no internal user exists yet, create a basic one
      if (!user.internalUser) {
        updatedUser.internalUser = {
          email: user.keycloakUser.email,
          isAdmin: editData.isAdmin,
          isReadonly: editData.isReadonly,
          roles: editData.isAdmin ? ['ROLE_ADMIN', 'ROLE_USER'] : ['ROLE_USER'],
          companies: companies.filter(company => 
            editData.selectedCompanies.includes(company.id)
          )
        };
      }

      await UsersService.updateUser(user.keycloakUser.id, updatedUser, token);
      
      // Refresh user data
      await fetchUserDetails();
      setIsEditing(false);
      setSuccessMessage('Gebruikersgegevens succesvol bijgewerkt!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij opslaan van gebruikersgegevens: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset edit data to current user data
    if (user) {
      setEditData({
        isAdmin: user.internalUser?.isAdmin || false,
        selectedCompanies: user.internalUser?.companies?.map(c => c.id) || []
      });
    }
  };

  const handleCompanyToggle = (companyId) => {
    setEditData(prev => ({
      ...prev,
      selectedCompanies: prev.selectedCompanies.includes(companyId)
        ? prev.selectedCompanies.filter(id => id !== companyId)
        : [...prev.selectedCompanies, companyId]
    }));
  };

  const handleCreateNewCompany = async () => {
    if (!newCompanyName.trim()) {
      setError('Bedrijfsnaam is verplicht');
      return;
    }

    setCreatingCompany(true);
    setError(null);
    try {
      const newCompany = await CompaniesService.createCompany({ name: newCompanyName.trim() }, token);
      
      // Refresh companies list to get the updated data
      await fetchCompanies();
      
      // If the API returned company data, auto-select it and use its info
      if (newCompany?.id) {
        setEditData(prev => ({
          ...prev,
          selectedCompanies: [...prev.selectedCompanies, newCompany.id]
        }));
        setSuccessMessage(`Bedrijf "${newCompany.name}" succesvol aangemaakt en toegevoegd!`);
      } else {
        // If API returned 204, we don't have the ID, so just show a generic success message
        setSuccessMessage(`Bedrijf "${newCompanyName.trim()}" succesvol aangemaakt!`);
      }
      
      // Reset form
      setIsCreatingCompany(false);
      setNewCompanyName('');
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij aanmaken van bedrijf: ' + (err.message || err));
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleCancelCreateCompany = () => {
    setIsCreatingCompany(false);
    setNewCompanyName('');
    setError(null);
  };

  const handleActivateUser = async () => {
    if (!user?.keycloakUser?.id) return;

    const action = user.keycloakUser.enabled ? 'deactiveren' : 'activeren';
    if (!window.confirm(`Weet je zeker dat je deze gebruiker wilt ${action}?`)) {
      return;
    }

    setActivating(true);
    setError(null);
    try {
      if (user.keycloakUser.enabled) {
        await UsersService.disableUser(user.keycloakUser.id, token);
        setSuccessMessage('Gebruiker succesvol gedeactiveerd!');
      } else {
        await UsersService.enableUser(user.keycloakUser.id, token);
        setSuccessMessage('Gebruiker succesvol geactiveerd!');
      }
      
      // Refresh user details to get updated status
      await fetchUserDetails();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Fout bij ${action} van gebruiker: ` + (err.message || err));
    } finally {
      setActivating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setError('Nieuw wachtwoord is verplicht');
      return;
    }

    if (!user?.keycloakUser?.id) return;

    setResetting(true);
    setError(null);
    try {
      await UsersService.resetPassword(user.keycloakUser.id, newPassword, token);
      setIsResettingPassword(false);
      setNewPassword('');
      setSuccessMessage('Wachtwoord succesvol gereset!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij resetten van wachtwoord: ' + (err.message || err));
    } finally {
      setResetting(false);
    }
  };

  const handleCancelPasswordReset = () => {
    setIsResettingPassword(false);
    setNewPassword('');
    setError(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Onbekend';
    return new Date(timestamp).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRoles = (roles) => {
    if (!roles || roles.length === 0) return 'Geen rollen';
    return roles.join(', ');
  };

  const formatCompanies = (companies) => {
    if (!companies || companies.length === 0) return 'Geen bedrijven';
    return companies.map(company => company.name || 'Onbekend bedrijf').join(', ');
  };

  if (userLoading || loading) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser?.isAdmin) {
    return <DeniedCard />;
  }

  return (
    <AdminShell
      title="Gebruikersdetails"
      backHref="/gebruikers"
      backLabel="Gebruikers"
      actions={(
        <button type="button" onClick={fetchUserDetails} disabled={loading} className="btn-ghost">
          Vernieuwen
        </button>
      )}
    >
        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {!user && !loading ? (
          <div className="text-center py-12">
            <span className="material-icons-outlined text-gray-400 text-6xl mb-4">person_off</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Gebruiker niet gevonden</h3>
            <p className="text-gray-600 mb-6">De opgegeven gebruiker bestaat niet of kon niet worden geladen.</p>
            <a 
              href="/gebruikers"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Terug naar gebruikerslijst
            </a>
          </div>
        ) : user && (
          <div className="space-y-6">
            {/* User Overview Card */}
            <div className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                    <span className="material-icons-outlined text-gray-600 text-2xl">person</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.keycloakUser?.firstName || user.keycloakUser?.lastName 
                        ? `${user.keycloakUser?.firstName || ''} ${user.keycloakUser?.lastName || ''}`.trim()
                        : 'Onbekende gebruiker'
                      }
                    </h2>
                    <p className="text-lg text-gray-600">{user.keycloakUser?.email || user.internalUser?.email || 'Geen email'}</p>
                    <div className="flex gap-2 mt-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.keycloakUser?.enabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.keycloakUser?.enabled ? 'Actief' : 'Inactief'}
                      </span>
                      {user.internalUser?.isAdmin && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Administrator
                        </span>
                      )}
                      {user.internalUser?.isReadonly && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          Alleen lezen
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Gebruikersinformatie</h3>
                  {isEditing && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      Bewerken
                    </span>
                  )}
                </div>
              </div>
              <div className="px-6 py-4">
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Account aangemaakt</dt>
                    <dd className="text-sm text-gray-900">
                      {formatDate(user.keycloakUser?.createdTimestamp)}
                    </dd>
                  </div>
                  {user.internalUser && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Rollen</dt>
                        <dd className="text-sm text-gray-900">{formatRoles(user.internalUser.roles)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Administrator</dt>
                        <dd className="text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (isEditing ? editData.isAdmin : user.internalUser?.isAdmin)
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(isEditing ? editData.isAdmin : user.internalUser?.isAdmin) ? 'Ja' : 'Nee'}
                          </span>
                          {isEditing && editData.isAdmin !== user.internalUser?.isAdmin && (
                            <span className="ml-2 text-xs text-blue-600">
                              (wordt gewijzigd)
                            </span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Alleen lezen</dt>
                        <dd className="text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (isEditing ? editData.isReadonly : user.internalUser?.isReadonly)
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(isEditing ? editData.isReadonly : user.internalUser?.isReadonly) ? 'Ja' : 'Nee'}
                          </span>
                          {isEditing && editData.isReadonly !== user.internalUser?.isReadonly && (
                            <span className="ml-2 text-xs text-blue-600">
                              (wordt gewijzigd)
                            </span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Bedrijven</dt>
                        <dd className="text-sm text-gray-900">
                          {isEditing ? (
                            <div>
                              {editData.selectedCompanies.length === 0 ? (
                                'Geen bedrijven geselecteerd'
                              ) : (
                                <div className="space-y-1">
                                  {companies
                                    .filter(c => editData.selectedCompanies.includes(c.id))
                                    .map(company => (
                                      <div key={company.id} className="flex items-center">
                                        <span className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                          {company.name}
                                        </span>
                                      </div>
                                    ))
                                  }
                                </div>
                              )}
                              {(() => {
                                const currentCompanyIds = user.internalUser?.companies?.map(c => c.id) || [];
                                const hasChanges = 
                                  editData.selectedCompanies.length !== currentCompanyIds.length ||
                                  !editData.selectedCompanies.every(id => currentCompanyIds.includes(id));
                                return hasChanges && (
                                  <span className="block text-xs text-blue-600 mt-1">
                                    (wijzigingen in bedrijven)
                                  </span>
                                );
                              })()}
                            </div>
                          ) : (
                            formatCompanies(user.internalUser.companies)
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Toegevoegd op</dt>
                        <dd className="text-sm text-gray-900">
                          {user.internalUser.createdAt 
                            ? new Date(user.internalUser.createdAt).toLocaleString('nl-NL')
                            : 'Onbekend'
                          }
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Laatst bijgewerkt</dt>
                        <dd className="text-sm text-gray-900">
                          {user.internalUser.updatedAt 
                            ? new Date(user.internalUser.updatedAt).toLocaleString('nl-NL')
                            : 'Onbekend'
                          }
                        </dd>
                      </div>
                    </>
                  )}
                  {!user.internalUser && isEditing && (editData.isAdmin || editData.isReadonly || editData.selectedCompanies.length > 0) && (
                    <div className="bg-blue-50 p-3 rounded-md">
                      <p className="text-blue-700 text-sm">
                        Er wordt een database record aangemaakt bij het opslaan.
                      </p>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Actions */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Acties</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setIsEditing(true)}
                  disabled={saving || activating}
                  className="btn-primary"
                >
                  <IconEdit size={16} />
                  Bewerken
                </button>
                <button 
                  onClick={handleActivateUser}
                  disabled={activating || saving}
                  className={`px-4 py-2 text-white rounded transition-colors disabled:bg-gray-400 ${
                    user.keycloakUser?.enabled 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {activating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      {user.keycloakUser?.enabled ? 'Deactiveren...' : 'Activeren...'}
                    </>
                  ) : (
                    <>
                      <span className="material-icons-outlined mr-2 text-sm">
                        {user.keycloakUser?.enabled ? 'block' : 'check_circle'}
                      </span>
                      {user.keycloakUser?.enabled ? 'Deactiveren' : 'Activeren'}
                    </>
                  )}
                </button>
                <button 
                  onClick={() => setIsResettingPassword(true)}
                  disabled={saving || activating || resetting}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:bg-gray-400"
                >
                  <span className="material-icons-outlined mr-2 text-sm">lock_reset</span>
                  Wachtwoord resetten
                </button>
              </div>
            </div>

            {/* Edit Modal */}
            {isEditing && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium text-gray-900">
                        Gebruiker bewerken: {user.keycloakUser?.firstName} {user.keycloakUser?.lastName}
                      </h3>
                      <button
                        onClick={handleCancel}
                        disabled={saving || creatingCompany}
                        className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                      >
                        <span className="material-icons-outlined">close</span>
                      </button>
                    </div>
                  </div>

                  <div className="px-6 py-4 space-y-6">
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Voornaam
                        </label>
                        <input
                          type="text"
                          value={editData.firstName}
                          onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                          disabled={saving || creatingCompany}
                          placeholder="Voornaam"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Achternaam
                        </label>
                        <input
                          type="text"
                          value={editData.lastName}
                          onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                          disabled={saving || creatingCompany}
                          placeholder="Achternaam"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                    </div>

                    {/* Admin Status */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editData.isAdmin}
                          onChange={(e) => setEditData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                          disabled={saving || creatingCompany}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Administrator rechten</span>
                          <p className="text-xs text-gray-500">Geeft de gebruiker volledige toegang tot het systeem</p>
                        </div>
                      </label>
                    </div>

                    {/* Readonly Status */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editData.isReadonly}
                          onChange={(e) => setEditData(prev => ({ ...prev, isReadonly: e.target.checked }))}
                          disabled={saving || creatingCompany}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Alleen lezen rechten</span>
                          <p className="text-xs text-gray-500">Gebruiker kan alleen gegevens bekijken, niet wijzigen</p>
                        </div>
                      </label>
                    </div>

                    {/* Companies */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Bedrijven</h4>
                        <button
                          onClick={() => setIsCreatingCompany(true)}
                          disabled={saving || creatingCompany}
                          className="inline-flex items-center px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400"
                        >
                          <span className="material-icons-outlined mr-1 text-xs">add</span>
                          Nieuw bedrijf
                        </button>
                      </div>
                      
                      {isCreatingCompany && (
                        <div className="mb-4 p-3 border border-green-200 rounded-md bg-green-50">
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Nieuw bedrijf aanmaken</h5>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newCompanyName}
                              onChange={(e) => setNewCompanyName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !creatingCompany && newCompanyName.trim()) {
                                  handleCreateNewCompany();
                                }
                              }}
                              disabled={creatingCompany}
                              placeholder="Bedrijfsnaam..."
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                            />
                            <button
                              onClick={handleCreateNewCompany}
                              disabled={creatingCompany || !newCompanyName.trim()}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400"
                            >
                              {creatingCompany ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              ) : (
                                'Aanmaken'
                              )}
                            </button>
                            <button
                              onClick={handleCancelCreateCompany}
                              disabled={creatingCompany}
                              className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                            >
                              Annuleren
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {companies.length === 0 ? (
                        <p className="text-sm text-gray-500">Geen bedrijven beschikbaar</p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                          {companies.map(company => (
                            <label key={company.id} className="flex items-start">
                              <input
                                type="checkbox"
                                checked={editData.selectedCompanies.includes(company.id)}
                                onChange={() => handleCompanyToggle(company.id)}
                                disabled={saving || creatingCompany}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 mt-0.5"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="text-sm font-medium text-gray-900">{company.name}</span>
                                {company.createdAt && (
                                  <p className="text-xs text-gray-500">
                                    Aangemaakt: {new Date(company.createdAt).toLocaleDateString('nl-NL')}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Selecteer de bedrijven waarbij deze gebruiker hoort of maak een nieuw bedrijf aan
                      </p>
                    </div>

                    {/* Keyboard shortcuts help */}
                    <div className="bg-gray-50 rounded-md p-3">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Sneltoetsen:</span> Escape om te annuleren, Ctrl+S om op te slaan, Ctrl+N voor nieuw bedrijf
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                      onClick={handleCancel}
                      disabled={saving || creatingCompany}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || creatingCompany}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Opslaan...
                        </>
                      ) : (
                        <>
                          <span className="material-icons-outlined mr-2 text-sm">save</span>
                          Opslaan
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Password Reset Modal */}
        {isResettingPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Wachtwoord resetten</h3>
                  <button
                    onClick={handleCancelPasswordReset}
                    disabled={resetting}
                    className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                  >
                    <span className="material-icons-outlined">close</span>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nieuw wachtwoord *
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={resetting}
                    placeholder="Voer nieuw wachtwoord in..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    De gebruiker moet dit nieuwe wachtwoord gebruiken bij de volgende inlog.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={handleCancelPasswordReset}
                  disabled={resetting}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting || !newPassword.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:bg-gray-400 flex items-center"
                >
                  {resetting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Resetten...
                    </>
                  ) : (
                    <>
                      <span className="material-icons-outlined mr-2 text-sm">lock_reset</span>
                      Wachtwoord resetten
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminShell>
  );
}
